// ============================================================
// EmergencyPackCard.js — the per-client emergency-pack push
// (business continuity phase 2, 2026-08-28; phase 3 Google Drive target,
// 2026-09-10).
//
// RULED (Zuber): the target is a PROPER SharePoint site in the CLIENT's
// tenant — a real site they administer and back up — so the pack survives
// every failure mode of ours and stays readable with their own credentials.
// Phase 3 adds the Google Workspace equivalent: a Shared Drive (or folder)
// the client added our SEPARATE push-only service account to as a
// Content manager, so the search account stays read-only by construction.
//
// 🔴 REPORTS THE PLATFORM GATES AS WELL AS THE PER-CLIENT ONE (the
// SharedCalendarCard rule): several things must all be true before a single
// file is written — kill switch, (SharePoint) app registration + tenant
// allowlist, and this row — and a page that shows only the piece it owns is
// how a correct configuration reads as broken. The kill switch and allowlist
// are deliberate main.bicep commits, never editable here.
//
// The push STAMPS are the card's health surface: an enabled client whose
// last success is old or absent is the exact silent-failure state the
// staleness monitor alerts on — the operator should be able to see the
// same fact here.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Copy } from 'lucide-react';
import Field from '../../components/Field';
import Switch from '../../components/Switch';
import { toast } from '../../components/Toaster';
import {
  getClientEmergencyPack,
  putClientEmergencyPack,
  emergencyPackAction,
} from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

function fmtStamp(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

export default function EmergencyPackCard({ clientId }) {
  const [enabled, setEnabled] = useState(false);
  const [targetType, setTargetType] = useState('sharepoint');
  const [siteUrl, setSiteUrl] = useState('');
  const [googleFolderId, setGoogleFolderId] = useState('');
  const [gates, setGates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);        // 'test' | 'push' | 'targets'
  const [probe, setProbe] = useState(null);
  const [pushResult, setPushResult] = useState(null);
  const [targets, setTargets] = useState(null);  // Google: the attributed candidate drives
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getClientEmergencyPack(clientId);
      setEnabled(!!res.enabled);
      setTargetType(res.targetType || 'sharepoint');
      setSiteUrl(res.siteUrl || '');
      setGoogleFolderId(res.googleFolderId || '');
      setGates(res);
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const isGoogle = targetType === 'google_drive';

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await putClientEmergencyPack(clientId, isGoogle
        ? { enabled, targetType: 'google_drive', googleFolderId: googleFolderId.trim() || null }
        : { enabled, targetType: 'sharepoint', siteUrl: siteUrl.trim() || null });
      toast.success('Emergency pack settings saved');
      // Re-read rather than trusting local state: the gate summary and the
      // stamps are server-derived and a changed target clears its stamps.
      setProbe(null); setPushResult(null);
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setSaving(false); }
  }

  async function runAction(kind, body) {
    setBusy(kind);
    try {
      const res = await emergencyPackAction(clientId, body);
      return res;
    } catch (err) { toast.error(translateError(err)); return null; }
    finally { setBusy(null); }
  }

  async function testConnection() {
    const res = await runAction('test', { action: 'test_connection' });
    if (!res) return;
    setProbe(res.result);
    if (res.result && res.result.found) {
      toast.success(isGoogle ? 'Drive reached' : 'Site reached');
      // The probe stamps the resolution server-side; show it.
      await fetchData();
    } else {
      toast.error(isGoogle ? 'That Google Drive target could not be found' : 'That SharePoint site could not be found');
    }
  }

  async function pushNow() {
    const res = await runAction('push', { action: 'push_now' });
    if (!res) return;
    toast.success(`Pack pushed: ${res.files} file${res.files === 1 ? '' : 's'} written${res.deleted ? `, ${res.deleted} stale removed` : ''}`);
    setPushResult(res);
    await fetchData();
  }

  async function loadTargets() {
    const res = await runAction('targets', { action: 'list_targets' });
    if (!res) return;
    setTargets(res);
    if (res.error) toast.error(res.error.message || 'The continuity drives could not be listed');
  }

  async function copy(text) {
    try { await navigator.clipboard.writeText(text); toast.success('Copied to clipboard.'); }
    catch { toast.error('Copy failed - select and copy manually.'); }
  }

  if (loading) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;
  if (error) return <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>;

  // Gates differ by target: the SharePoint push needs the Document Auth reg and
  // the tenant allowlist; the Google push needs the client to BE a Workspace
  // client (its domain is the scope key). Both need the kill switch.
  const platformReady = gates && gates.platformEnabled && (isGoogle
    ? !!gates.googleWorkspaceDomain
    : (gates.appRegistrationConfigured && gates.tenantAllowlisted));

  return (
    <div className="pc-form">
      {gates && !platformReady && (
        <StatusMessage as="p" variant="error" className="pc-state pc-state--error" data-testid="emergency-pack-gates">
          {'Platform gates not open: '}
          {[
            !gates.platformEnabled && 'the kill switch (emergencyPackPushEnabled) is off',
            !isGoogle && !gates.appRegistrationConfigured && 'no app registration is configured',
            !isGoogle && !gates.tenantAllowlisted && (gates.externalTenantId
              ? 'this tenant is not in the allowlist'
              : 'this client has no active federation config, and the push needs a tenant relationship'),
            isGoogle && !gates.googleWorkspaceDomain && 'this client has no active Google Workspace integration, and a Drive target is attributed by its Workspace domain (connect Workspace search on the Tenant search tab first)',
          ].filter(Boolean).join('; ')}
          {isGoogle
            ? '. See docs/operations/emergency-pack-gdrive-setup.md.'
            : '. These are main.bicep changes, not settings on this page. See docs/operations/emergency-pack-spo-setup.md.'}
        </StatusMessage>
      )}

      <form onSubmit={submit}>
        <Field
          label="Emergency pack push enabled"
          help="The per-client gate. Off = CareHub writes nothing to this client's site or drive, whatever else is configured. Turning it OFF does not delete the files already there - the copies are the client's own continuity artefact."
        >
          <Switch checked={enabled} onChange={setEnabled} ariaLabel="Emergency pack push enabled" />
        </Field>

        <Field label="Destination" help="Where the nightly pack is written. A Microsoft 365 client gets a SharePoint site; a Google Workspace client gets a Shared Drive our continuity account has been added to. Changing the kind clears the other kind's target and its stamps.">
          <select value={targetType} onChange={(e) => { setTargetType(e.target.value); setProbe(null); setTargets(null); }} data-testid="emergency-pack-target-type">
            <option value="sharepoint">SharePoint site (Microsoft 365)</option>
            <option value="google_drive">Google Shared Drive (Google Workspace)</option>
          </select>
        </Field>

        {!isGoogle && (
          <Field
            label="SharePoint site URL"
            required={enabled}
            help="The site in the CLIENT's tenant that receives the nightly pack, e.g. https://theirtenant.sharepoint.com/sites/CareHubEmergency. It must already exist, be restricted to named staff, and be granted to the Document Auth app with a per-site Sites.Selected grant. Saving a CHANGED URL clears the stamped site id; the next test or push re-resolves."
          >
            <input
              type="text"
              maxLength={1024}
              value={siteUrl}
              placeholder="https://theirtenant.sharepoint.com/sites/CareHubEmergency"
              onChange={(e) => setSiteUrl(e.target.value)}
              data-testid="emergency-pack-site-url"
            />
          </Field>
        )}

        {isGoogle && (
          <>
            <div className="pc-actions">
              <button type="button" className="pc-btn-secondary" disabled={!!busy} onClick={loadTargets} data-testid="emergency-pack-load-targets">
                {busy === 'targets' ? 'Loading…' : 'Load continuity drives'}
              </button>
            </div>
            {targets && targets.pushServiceAccountEmail && (
              <StatusMessage as="div" variant="empty" className="pc-state" data-testid="emergency-pack-push-account">
                {'The client adds this address as a Content manager on ONE Shared Drive for continuity copies (Contributor cannot delete, and the pack removes leavers; it is a different account from the read-only search one): '}
                <code>{targets.pushServiceAccountEmail}</code>
                {' '}
                <button type="button" className="pc-btn-secondary" onClick={() => copy(targets.pushServiceAccountEmail)} aria-label="Copy continuity account address"><Copy size={14} /> Copy</button>
              </StatusMessage>
            )}
            {targets && targets.error && (
              <StatusMessage as="p" variant="error" className="pc-state pc-state--error" data-testid="emergency-pack-targets-error">
                {targets.error.code === 'not_configured'
                  ? 'Our side: the continuity push account key is not configured on the platform yet (see docs/operations/emergency-pack-gdrive-setup.md).'
                  : (targets.error.message || 'The continuity drives could not be listed.')}
              </StatusMessage>
            )}
            <Field
              label="Continuity Shared Drive"
              required={enabled}
              help={`Only drives the continuity account has been added to AND that are managed by someone in the client's Workspace domain${gates && gates.googleWorkspaceDomain ? ` (${gates.googleWorkspaceDomain})` : ''} are offered, so a drive can never be attached to the wrong client. Pick the drive, Save, then Test connection.`}
            >
              {targets && Array.isArray(targets.targets) && targets.targets.length > 0 ? (
                <select value={googleFolderId} onChange={(e) => setGoogleFolderId(e.target.value)} data-testid="emergency-pack-google-target">
                  <option value="">Choose a drive…</option>
                  {targets.targets.map((t) => (
                    <option key={t.targetId} value={t.targetId} disabled={!t.writable}>
                      {t.name}{t.writable ? '' : (t.canAddChildren ? ' (Contributor - needs Content manager)' : ' (view only - needs Content manager)')}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  maxLength={128}
                  value={googleFolderId}
                  placeholder={targets && Array.isArray(targets.targets) ? 'No attributed drive found yet - the client has not added the account (step 1)' : 'Load continuity drives to pick one'}
                  onChange={(e) => setGoogleFolderId(e.target.value)}
                  data-testid="emergency-pack-google-folder-id"
                />
              )}
            </Field>
            {targets && typeof targets.unattributedCount === 'number' && targets.unattributedCount > 0 && (
              <StatusMessage as="p" variant="empty" className="pc-state" data-testid="emergency-pack-unattributed">
                {`${targets.unattributedCount} other drive${targets.unattributedCount === 1 ? '' : 's'} shared with the continuity account ${targets.unattributedCount === 1 ? 'is' : 'are'} managed from another domain and not shown.`}
              </StatusMessage>
            )}
          </>
        )}

        <div className="pc-actions">
          <button type="submit" className="pc-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>

      {gates && (
        <StatusMessage as="p" variant="empty" className="pc-state" data-testid="emergency-pack-stamps">
          {[
            isGoogle
              ? (gates.googleFolderName ? `Drive resolved: ${gates.googleFolderName}.` : 'Drive not yet resolved - run Test connection.')
              : (gates.siteId ? 'Site resolved (id stamped).' : 'Site not yet resolved - run Test connection.'),
            gates.lastSuccessAt
              ? `Last successful push ${fmtStamp(gates.lastSuccessAt)}.`
              : 'No successful push yet.',
            gates.lastError ? `Last error: ${gates.lastError}` : null,
          ].filter(Boolean).join(' ')}
        </StatusMessage>
      )}

      <div className="pc-actions">
        <button
          type="button"
          className="pc-btn-secondary"
          disabled={!!busy || !enabled}
          onClick={testConnection}
          data-testid="emergency-pack-test"
        >
          {busy === 'test' ? 'Testing…' : 'Test connection'}
        </button>
        <button
          type="button"
          className="pc-btn-secondary"
          disabled={!!busy || !enabled}
          onClick={pushNow}
          data-testid="emergency-pack-push"
        >
          {busy === 'push' ? 'Pushing…' : 'Push now'}
        </button>
      </div>

      {probe && (
        <StatusMessage as="p" variant="empty" className="pc-state" data-testid="emergency-pack-probe">
          {probe.found
            ? `Reached ${probe.webUrl || (isGoogle ? 'the drive' : 'the site')}${probe.displayName ? ` (${probe.displayName})` : ''}. This checks the ${isGoogle ? "drive's" : "site's"} own details, never anything in it.`
            : `Not found. ${probe.detail || probe.reason}`}
        </StatusMessage>
      )}

      {pushResult && (
        <StatusMessage as="p" variant="empty" className="pc-state" data-testid="emergency-pack-push-result">
          {`Pushed the combined pack plus ${pushResult.people} person file${pushResult.people === 1 ? '' : 's'} to `}
          {pushResult.webUrl
            ? <a href={pushResult.webUrl} target="_blank" rel="noreferrer">{pushResult.webUrl}</a>
            : (isGoogle ? 'the drive' : 'the site')}
          {pushResult.deleted ? `. Removed ${pushResult.deleted} stale person file${pushResult.deleted === 1 ? '' : 's'}.` : '.'}
        </StatusMessage>
      )}
    </div>
  );
}
